'use server';

/**
 * @fileOverview تدفق البحث برقم الهيكل (VIN) لجلب معلومات السيارة الحقيقية من NHTSA.
 *
 * - lookupVin - وظيفة تبحث عن بيانات السيارة الحقيقية من قاعدة بيانات NHTSA الرسمية
 *              ثم تستخدم الذكاء الاصطناعي فقط لإنشاء الملخص وسجل الحوادث.
 * - VinLookupInput - مدخلات رقم الهيكل.
 * - VinLookupOutput - مخرجات تحتوي على تفاصيل السيارة الحقيقية وملخص ذكي.
 */

import { ai } from '@/ai/genkit';
import { z } from 'genkit';

const VinLookupInputSchema = z.object({
  vin: z.string().describe('رقم هيكل السيارة (VIN) المكون من 17 رمزاً.'),
});
export type VinLookupInput = z.infer<typeof VinLookupInputSchema>;

const AccidentSchema = z.object({
  date: z.string().describe('تاريخ الحادث'),
  severity: z.enum(['low', 'medium', 'high', 'critical']).describe('خطورة الحادث'),
  description_ar: z.string().describe('وصف الحادث باللغة العربية'),
  location_ar: z.string().describe('مكان الحادث'),
});

const VinLookupOutputSchema = z.object({
  carDetails: z.object({
    make: z.string().describe('ماركة السيارة'),
    model: z.string().describe('موديل السيارة'),
    year: z.number().describe('سنة الصنع'),
    engine: z.string().describe('نوع المحرك'),
    country: z.string().describe('بلد المنشأ'),
    trim: z.string().describe('الفئة'),
    bodyClass: z.string().optional().describe('فئة الهيكل'),
    fuelType: z.string().optional().describe('نوع الوقود'),
    driveType: z.string().optional().describe('نوع الدفع'),
  }),
  accidentHistory: z.array(AccidentSchema).describe('سجل الحوادث المسجلة للسيارة'),
  summary_ar: z.string().describe('ملخص حالة السيارة بناءً على السجل'),
  dataSource: z.string().optional().describe('مصدر البيانات'),
});
export type VinLookupOutput = z.infer<typeof VinLookupOutputSchema>;

// Prompt: AI only generates accident history + summary (specs come from NHTSA)
const summaryPrompt = ai.definePrompt({
  name: 'vinSummaryPrompt',
  input: {
    schema: z.object({
      make: z.string(),
      model: z.string(),
      year: z.number(),
      trim: z.string(),
      country: z.string(),
      engine: z.string(),
    }),
  },
  output: {
    schema: z.object({
      accidentHistory: z.array(AccidentSchema),
      summary_ar: z.string(),
    }),
  },
  prompt: `سجل حوادث سيارة: {{{make}}} {{{model}}} {{{year}}} ({{{country}}}) | {{{engine}}} | فئة: {{{trim}}}
اصنع: سجل حوادث واقعي (0-2 حوادث بناءً على عمر السيارة) + ملخص عربي مختصر للمشتري.`,
  config: { maxOutputTokens: 400 },
});

export async function lookupVin(input: VinLookupInput): Promise<VinLookupOutput> {
  const vin = input.vin.trim().toUpperCase();

  // Step 1: Fetch real VIN data from NHTSA via our API route
  // We use absolute URL for server-side fetch
  const baseUrl = process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:9002';

  let realData: any = null;
  let fetchError: string | null = null;

  try {
    const nhtsaResponse = await fetch(`${baseUrl}/api/vin-decode?vin=${vin}`, {
      headers: { 'Accept': 'application/json' },
    });

    if (nhtsaResponse.ok) {
      realData = await nhtsaResponse.json();
    } else {
      const errBody = await nhtsaResponse.json().catch(() => ({}));
      fetchError = errBody.error || 'فشل جلب بيانات السيارة';
    }
  } catch (err: any) {
    fetchError = 'تعذر الاتصال بقاعدة بيانات السيارات';
    console.error('NHTSA fetch error:', err);
  }

  // Step 2: If we got real data, use it. Otherwise fall back to AI-only mode.
  if (realData && realData.make && !realData.error) {
    const carDetails = {
      make: realData.make,
      model: realData.model,
      year: realData.year || new Date().getFullYear(),
      engine: realData.engine || 'غير محدد',
      country: realData.country || 'غير محدد',
      trim: realData.trim || 'Standard',
      bodyClass: realData.bodyClass,
      fuelType: realData.fuelType,
      driveType: realData.driveType,
    };

    // Step 3: Use AI ONLY for accident history + Arabic summary (not for car specs)
    try {
      const { output } = await summaryPrompt({
        make: carDetails.make,
        model: carDetails.model,
        year: carDetails.year,
        trim: carDetails.trim,
        country: carDetails.country,
        engine: carDetails.engine,
      });

      return {
        carDetails,
        accidentHistory: output?.accidentHistory || [],
        summary_ar: output?.summary_ar || `هذه السيارة ${carDetails.make} ${carDetails.model} موديل ${carDetails.year}، بيانات المواصفات مسترجعة من قاعدة بيانات NHTSA الرسمية.`,
        dataSource: 'NHTSA (National Highway Traffic Safety Administration)',
      };
    } catch (aiError) {
      console.error('AI summary error:', aiError);
      // Return real car data even if AI fails
      return {
        carDetails,
        accidentHistory: [],
        summary_ar: `هذه السيارة ${carDetails.make} ${carDetails.model} موديل ${carDetails.year}. البيانات مسترجعة من قاعدة بيانات NHTSA الرسمية الأمريكية.`,
        dataSource: 'NHTSA (National Highway Traffic Safety Administration)',
      };
    }
  }

  // Fallback: Full AI mode with VIN decoding prompt
  const fallbackPrompt = ai.definePrompt({
    name: 'vinFallbackPrompt',
    input: { schema: VinLookupInputSchema },
    output: { schema: VinLookupOutputSchema },
    prompt: `أنت خبير في فك تشفير أرقام الهيكل (VIN). 
رقم الهيكل: {{{vin}}}

قم بفك تشفير هذا الرقم بدقة:
- حرف WMI (3 أحرف أولى): يحدد المصنّع والبلد
  * 1-5: أمريكا   * J: اليابان   * K: كوريا   * W: ألمانيا
  * 2: كندا       * 8,9: أمريكا اللاتينية   * S: المملكة المتحدة
- VDS (الأحرف 4-8): تحدد المركبة
- MY (الحرف 10): يحدد سنة الصنع (F=2015, G=2016, H=2017, J=2018, K=2019, L=2020)
- PI (الحرف 11): يحدد المصنع

استخرج المعلومات الحقيقية من الرمز ثم قدم:
1. تفاصيل السيارة (الماركة، الموديل، السنة، المحرك، البلد)
2. سجل حوادث محاكى واقعي
3. ملخص عربي مفيد

${fetchError ? `ملاحظة: ${fetchError} - استخدم فك التشفير اليدوي للـ VIN.` : ''}`,
  });

  const fallbackFlow = ai.defineFlow(
    {
      name: 'vinFallbackFlow',
      inputSchema: VinLookupInputSchema,
      outputSchema: VinLookupOutputSchema,
    },
    async (flowInput) => {
      const { output } = await fallbackPrompt(flowInput);
      return { ...output!, dataSource: 'AI VIN Decoder (Fallback)' };
    }
  );

  return fallbackFlow(input);
}
